import type { Profile as ProfileData, ProgressionInfo, User } from "../../../api/types";
import { Badge } from "../../components/Badge";
import { Progress } from "../../components/Progress";
import { cx } from "../../cx";
import { formatInt } from "../../format";
import { Lcd } from "./Lcd";
import { NameEditor } from "./NameEditor";
import { nextRankTier } from "./profileStats";

export interface IdentityPanelProps {
  user: User;
  profile: ProfileData;
  progression: ProgressionInfo;
}

/** Header block: name (with rename), level and XP, rank tier with rating and the tier ladder. */
export function IdentityPanel({ user, profile, progression }: IdentityPanelProps) {
  const isMaxLevel = profile.xpForNext === 0;
  const next = nextRankTier(progression.rankTiers, profile.rating);
  const currentTier = progression.rankTiers.find((tier) => tier.name === profile.rankTier);

  return (
    <section class="profile__identity" aria-label="Identity, level and rank">
      <NameEditor user={user} />

      <div class="profile__level">
        <div class="profile__level-main" role="group" aria-label={`Level ${profile.level} of ${progression.maxLevel}`}>
          <span class="eyebrow">Level</span>
          <span class="profile__level-line">
            <span class="profile__level-value lcd" data-ghost={"8".repeat(String(progression.maxLevel).length)}>
              {profile.level}
            </span>
            <span class="profile__level-cap">
              <Lcd text={`/ ${progression.maxLevel}`} />
            </span>
          </span>
        </div>
        <div class="profile__xp">
          <div class="profile__xp-line">
            {isMaxLevel ? (
              <strong class="profile__max">Max level</strong>
            ) : (
              <span>
                <Lcd text={`${formatInt(profile.xpIntoLevel)} / ${formatInt(profile.xpForNext)} XP`} />
              </span>
            )}
          </div>
          <Progress
            value={profile.xpIntoLevel}
            max={profile.xpForNext}
            tone={isMaxLevel ? "amber" : "cyan"}
            label={isMaxLevel ? "Max level reached" : `Experience towards level ${profile.level + 1}`}
          />
          <div class="profile__xp-foot">
            <span class="profile__xp-note muted num">
              {isMaxLevel
                ? "Level cap reached"
                : `${formatInt(profile.xpForNext - profile.xpIntoLevel)} XP to level ${profile.level + 1}`}
            </span>
            <span class="profile__xp-total">
              <Lcd text={`${formatInt(profile.xp)} total XP`} />
            </span>
          </div>
        </div>
      </div>

      <div class="profile__rank">
        <div class="profile__rank-line">
          <Badge tone="gold">{profile.rankTier}</Badge>
          <span class="profile__rating lcd">{formatInt(Math.floor(profile.rating))}</span>
          <span class="eyebrow">Rating</span>
          <span class="profile__rank-next muted num">
            {next ? `+${formatInt(next.remaining)} rating to ${next.name}` : "Top tier reached"}
          </span>
        </div>
        <ol class="profile__ladder" aria-label="Rank tiers">
          {progression.rankTiers.map((tier) => {
            const current = tier.name === profile.rankTier;
            const passed = currentTier !== undefined && tier.minRating < currentTier.minRating;
            return (
              <li
                key={tier.name}
                class={cx("profile__tier", current && "is-current", passed && "is-passed")}
                aria-current={current ? "true" : undefined}
                title={`${tier.name}: from ${formatInt(tier.minRating)} rating`}
              >
                {tier.name}
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}
